import React, { useState, useEffect } from 'react';
import { ConsoleInput, ConsoleLabel, ConsoleSelect, FormGroup, FieldError } from '../../styles/common/input.styled';
import { Button } from '../../styles/common/Button.styled';
import Modal from '../common/Modal';
import PageState from '../common/PageState';
import { useApi } from '../../hooks/useApi';
import { toast } from 'react-hot-toast';

interface QueueCreateModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const isValidTimeout = (value: number) => Number.isInteger(value) && value >= 1 && value <= 120;

const QueueCreateModal: React.FC<QueueCreateModalProps> = ({ onClose, onSuccess }) => {
  const { loading, request } = useApi();
  const [formData, setFormData] = useState({
    displayName: '',
    strategy: 'PARALLEL',
    ringTimeoutSeconds: 10,
    totalTimeoutSeconds: 30,
    audioFileId: ''
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [audioList, setAudioList] = useState<any[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let isCurrent = true;
    setIsInitialLoading(true);
    setHasLoadError(false);
    const fetchAudios = async () => {
      try {
        const audios = await request({ url: '/audio-source', params: { limit: 100, pg: 1 } });
        if (!isCurrent) return;
        setAudioList(audios?.content || (Array.isArray(audios) ? audios : []));
      } catch (error) {
        if (!isCurrent) return;
        console.error('Fetch audio sources error:', error);
        setHasLoadError(true);
      } finally {
        if (isCurrent) setIsInitialLoading(false);
      }
    };
    fetchAudios();
    return () => { isCurrent = false; };
  }, [request, retryKey]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
    setFormData(prev => ({
      ...prev,
      [name]: ['ringTimeoutSeconds', 'totalTimeoutSeconds'].includes(name) ? Number(value) : value
    }));
  };

  const handleCreate = async () => {
    if (isInitialLoading || hasLoadError) return;
    setFieldErrors({});
    if (!formData.displayName) {
      setFieldErrors({ displayName: '수신그룹명을 입력해주세요.' });
      toast.error('수신그룹명을 입력해주세요.');
      return;
    }

    const isSequential = formData.strategy === 'SEQUENTIAL';
    if (!isValidTimeout(formData.totalTimeoutSeconds) || (isSequential && !isValidTimeout(formData.ringTimeoutSeconds))) {
      setFieldErrors({
        ...(!isValidTimeout(formData.totalTimeoutSeconds) ? { totalTimeoutSeconds: '1초 이상 120초 이하의 정수로 입력해주세요.' } : {}),
        ...(isSequential && !isValidTimeout(formData.ringTimeoutSeconds) ? { ringTimeoutSeconds: '1초 이상 120초 이하의 정수로 입력해주세요.' } : {}),
      });
      toast.error('개별 벨 울림 시간과 전체 대기시간은 1초 이상 120초 이하의 정수로 입력해주세요.');
      return;
    }

    try {
      await request({
        url: '/ringing-group',
        method: 'POST',
        data: {
          displayName: formData.displayName,
          strategy: formData.strategy,
          totalTimeoutSeconds: formData.totalTimeoutSeconds,
          ...(isSequential ? { ringTimeoutSeconds: formData.ringTimeoutSeconds } : {}),
          ...(formData.audioFileId ? { audioFileId: Number(formData.audioFileId) } : {})
        }
      });
      toast.success('수신그룹이 생성되었습니다.');
      onSuccess();
    } catch (error) {
      console.error('Create queue error:', error);
      toast.error('수신그룹 생성에 실패했습니다.');
    }
  };

  return (
    <Modal title="신규 수신그룹 등록" onClose={onClose} historyMode="none" width={520} footer={<>
      <Button $variant="secondary" onClick={onClose}>취소</Button>
      <Button onClick={handleCreate} disabled={loading || isInitialLoading || hasLoadError}>{loading ? '등록 중...' : '등록하기'}</Button>
    </>}>
      {isInitialLoading && <PageState status="loading" title="선택 항목을 불러오는 중입니다." />}
      {hasLoadError && <PageState status="error" title="선택 항목을 불러오지 못했습니다." onRetry={() => setRetryKey(key => key + 1)} />}
        <FormGroup>
          <ConsoleLabel htmlFor="queue-display-name">수신그룹명</ConsoleLabel>
          <ConsoleInput
            id="queue-display-name"
            name="displayName"
            aria-invalid={Boolean(fieldErrors.displayName)}
            aria-describedby={fieldErrors.displayName ? 'queue-displayName-error' : undefined}
            value={formData.displayName}
            onChange={handleChange}
            placeholder="수신그룹명을 입력하세요"
          />
          {fieldErrors.displayName && <FieldError id="queue-displayName-error">{fieldErrors.displayName}</FieldError>}
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="queue-strategy">콜분배 방식</ConsoleLabel>
          <ConsoleSelect
            id="queue-strategy"
            name="strategy"
            value={formData.strategy}
            onChange={handleChange}
          >
            <option value="PARALLEL">동시콜</option>
            <option value="SEQUENTIAL">순차콜</option>
          </ConsoleSelect>
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="queue-audio-file-id">대기 음원</ConsoleLabel>
          <ConsoleSelect
            id="queue-audio-file-id"
            name="audioFileId"
            value={formData.audioFileId}
            onChange={handleChange}
          >
            <option value="">음원 선택 (없음)</option>
            {audioList.map(a => (
              <option key={a.id} value={a.id}>{a.name || a.originalFilename}</option>
            ))}
          </ConsoleSelect>
        </FormGroup>

        {formData.strategy === 'SEQUENTIAL' && (
          <FormGroup>
            <ConsoleLabel htmlFor="queue-ring-timeout-seconds">개별 벨 울림 시간 (초)</ConsoleLabel>
            <ConsoleInput
              id="queue-ring-timeout-seconds"
              name="ringTimeoutSeconds"
              aria-invalid={Boolean(fieldErrors.ringTimeoutSeconds)}
              aria-describedby={fieldErrors.ringTimeoutSeconds ? 'queue-ringTimeoutSeconds-error' : undefined}
              type="number"
              min={1}
              max={120}
              value={formData.ringTimeoutSeconds}
              onChange={handleChange}
              placeholder="개별 벨 울림 시간(초)"
            />
            {fieldErrors.ringTimeoutSeconds && <FieldError id="queue-ringTimeoutSeconds-error">{fieldErrors.ringTimeoutSeconds}</FieldError>}
          </FormGroup>
        )}

        <FormGroup>
          <ConsoleLabel htmlFor="queue-total-timeout-seconds">전체 대기시간 (초)</ConsoleLabel>
          <ConsoleInput
            id="queue-total-timeout-seconds"
            name="totalTimeoutSeconds"
            aria-invalid={Boolean(fieldErrors.totalTimeoutSeconds)}
            aria-describedby={fieldErrors.totalTimeoutSeconds ? 'queue-totalTimeoutSeconds-error' : undefined}
            type="number"
            min={1}
            max={120}
            value={formData.totalTimeoutSeconds}
            onChange={handleChange}
            placeholder="전체 대기시간(초)"
          />
          {fieldErrors.totalTimeoutSeconds && <FieldError id="queue-totalTimeoutSeconds-error">{fieldErrors.totalTimeoutSeconds}</FieldError>}
        </FormGroup>

    </Modal>
  );
};

export default QueueCreateModal;
